import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { PiArrowSquareOutBold, PiFileXlsBold, PiMagnifyingGlassBold, PiWarningOctagonDuotone } from 'react-icons/pi';
import { Button, LoadingState, PageHeader } from '@v2/design-system';
import api from '@v2/lib/api';
import { cn } from '@v2/utils/cn';
import { useI18n } from '@v2/providers/I18nProvider';
import { SortTh, useSortableRows } from '@v2/components/table/useSortableRows';
import SellerPodium from '@v2/components/ranking/SellerPodium';

type Severity = 'low' | 'medium' | 'high' | 'critical';
type Status = 'open' | 'renegotiated' | 'forfeited';

type Row = {
    installment_id: number;
    deal_id: number | null;
    sale_number: string | null;
    ploomes_number: string | null;
    deal_url: string | null;
    client: string | null;
    seller: string | null;
    installment_label: string;
    due_date: string;
    amount: number;
    days_overdue: number;
    severity: Severity;
    status: Status;
    flagged_at: string | null;
    forfeit_date: string;
    days_to_forfeit: number | null;
    open_defaults_in_deal: number;
    commission_blocked: number;
    sale_cancelled: boolean;
};

type Summary = {
    installments: number;
    amount: number;
    commission_blocked: number;
    deals: number;
    clients: number;
    avg_days: number;
    max_days: number;
    forfeit_this_month: number;
    by_severity: Array<{ key: Severity; from: number; to: number | null; count: number; amount: number }>;
    by_status: Record<Status, number>;
    top_sellers: RankRow[];
};

type RankRow = {
    position: number;
    seller: string;
    user_id: number | null;
    avatar_url: string | null;
    count: number;
    deals: number;
    amount: number;
    share_pct: number;
    max_days: number;
    commission_blocked: number;
    severity_mix: Record<Severity, number>;
};

/** Cores por criticidade (dias em atraso) — mesmas do Excel. */
const SEVERITY_STYLE: Record<Severity, { card: string; dot: string; badge: string; bar: string }> = {
    low: { card: 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30', dot: 'bg-amber-400', badge: 'bg-amber-100 text-amber-800 dark:bg-amber-950/60 dark:text-amber-200', bar: 'bg-amber-400' },
    medium: { card: 'border-orange-200 bg-orange-50 dark:border-orange-900/50 dark:bg-orange-950/30', dot: 'bg-orange-500', badge: 'bg-orange-100 text-orange-800 dark:bg-orange-950/60 dark:text-orange-200', bar: 'bg-orange-500' },
    high: { card: 'border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30', dot: 'bg-red-600', badge: 'bg-red-100 text-red-800 dark:bg-red-950/60 dark:text-red-200', bar: 'bg-red-600' },
    critical: { card: 'border-violet-300 bg-violet-50 dark:border-violet-900/50 dark:bg-violet-950/30', dot: 'bg-violet-700', badge: 'bg-violet-700 text-white', bar: 'bg-violet-700' },
};

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 2 }).format(v ?? 0);

const fmtDate = (iso?: string | null) => (iso ? new Date(`${iso}T12:00:00`).toLocaleDateString('pt-BR') : '—');

function Kpi({ label, value, hint, tone = 'neutral' }: { label: string; value: string; hint?: string; tone?: 'neutral' | 'red' | 'violet' | 'amber' }) {
    const tones = {
        neutral: 'border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900',
        red: 'border-red-200 bg-red-50 dark:border-red-900/50 dark:bg-red-950/30',
        violet: 'border-violet-200 bg-violet-50 dark:border-violet-900/50 dark:bg-violet-950/30',
        amber: 'border-amber-200 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-950/30',
    };

    return (
        <div className={cn('rounded-xl border p-4', tones[tone])}>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-400">{label}</p>
            <p className="mt-1 text-2xl font-black tabular-nums text-gray-900 dark:text-zinc-50">{value}</p>
            {hint && <p className="mt-0.5 text-xs text-gray-500">{hint}</p>}
        </div>
    );
}

export default function DefaultedInstallmentsPage() {
    const { t } = useI18n();
    const [search, setSearch] = useState('');
    const [severity, setSeverity] = useState<Severity | ''>('');
    const [status, setStatus] = useState<Status | ''>('');
    const [exporting, setExporting] = useState(false);

    const params = useMemo(() => ({
        search: search.trim() || undefined,
        severity: severity || undefined,
        status: status || undefined,
    }), [search, severity, status]);

    const query = useQuery({
        queryKey: ['defaulted-installments', params],
        queryFn: () => api.get('/defaulted-installments', { params }).then((r) => r.data as { data: Row[]; summary: Summary }),
        placeholderData: (prev) => prev,
    });

    const summary = query.data?.summary;
    const rows = query.data?.data ?? [];
    const saleKey = (v: string | null) => (v && /^\d+$/.test(v) ? Number(v) : v);
    const { sorted, sort, toggle } = useSortableRows(rows, {
        sale: (r) => saleKey(r.sale_number),
        client: (r) => r.client,
        seller: (r) => r.seller,
        installment: (r) => r.installment_label,
        due: (r) => r.due_date,
        amount: (r) => r.amount,
        days: (r) => r.days_overdue,
        status: (r) => r.status,
        forfeit: (r) => r.forfeit_date,
        commission: (r) => r.commission_blocked,
    }, { key: 'days', dir: 'desc' });
    const maxSeverityAmount = Math.max(1, ...(summary?.by_severity ?? []).map((s) => s.amount));

    const exportExcel = async () => {
        setExporting(true);
        try {
            const response = await api.get('/defaulted-installments/export', { params, responseType: 'blob' });
            const url = URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.download = `inadimplencias-${new Date().toISOString().slice(0, 10)}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            setTimeout(() => URL.revokeObjectURL(url), 1000);
        } catch {
            toast.error(t('v2.defaulted.export_failed'));
        } finally {
            setExporting(false);
        }
    };

    return (
        <div className="space-y-6">
            <PageHeader title={t('v2.defaulted.title')} subtitle={t('v2.defaulted.subtitle')}>
                <Button type="button" size="sm" variant="outline" isLoading={exporting} onClick={() => void exportExcel()}>
                    <PiFileXlsBold className="mr-1 size-4" aria-hidden /> {t('v2.defaulted.export')}
                </Button>
            </PageHeader>

            {query.isLoading || !summary ? (
                <LoadingState variant="dashboard" />
            ) : (
                <>
                    <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                        <Kpi label={t('v2.defaulted.kpi_installments')} value={String(summary.installments)}
                            hint={t('v2.defaulted.kpi_installments_hint', { deals: summary.deals, clients: summary.clients })} tone="red" />
                        <Kpi label={t('v2.defaulted.kpi_amount')} value={brl(summary.amount)} hint={t('v2.defaulted.kpi_amount_hint')} />
                        <Kpi label={t('v2.defaulted.kpi_commission')} value={brl(summary.commission_blocked)} hint={t('v2.defaulted.kpi_commission_hint')} tone="amber" />
                        <Kpi label={t('v2.defaulted.kpi_days')} value={t('v2.defaulted.days', { days: summary.avg_days })}
                            hint={t('v2.defaulted.kpi_days_hint', { max: summary.max_days, forfeit: summary.forfeit_this_month })} tone="violet" />
                    </div>

                    <section className="rounded-xl border border-gray-200 bg-white p-4 dark:border-zinc-700 dark:bg-zinc-900">
                        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                            <p className="text-sm font-bold text-gray-900 dark:text-zinc-50">{t('v2.defaulted.by_severity')}</p>
                            <div className="flex flex-wrap gap-2 text-[11px]">
                                {(['open', 'renegotiated', 'forfeited'] as const).map((st) => (
                                    <button
                                        key={st}
                                        type="button"
                                        onClick={() => setStatus(status === st ? '' : st)}
                                        className={cn(
                                            'rounded-full border px-2.5 py-1 font-semibold',
                                            status === st ? 'border-gray-900 bg-gray-900 text-white dark:border-white dark:bg-white dark:text-zinc-900' : 'border-gray-200 text-gray-600 dark:border-zinc-700 dark:text-zinc-300',
                                        )}
                                    >
                                        {t(`v2.defaulted.status_${st}`)} · {summary.by_status[st] ?? 0}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                            {summary.by_severity.map((sv) => (
                                <button
                                    key={sv.key}
                                    type="button"
                                    onClick={() => setSeverity(severity === sv.key ? '' : sv.key)}
                                    className={cn(
                                        'rounded-xl border p-3 text-left transition-shadow',
                                        SEVERITY_STYLE[sv.key].card,
                                        severity === sv.key && 'ring-2 ring-offset-1 ring-gray-900 dark:ring-white',
                                    )}
                                >
                                    <p className="flex items-center gap-1.5 text-xs font-bold text-gray-800 dark:text-zinc-100">
                                        <span className={cn('size-2.5 rounded-full', SEVERITY_STYLE[sv.key].dot)} />
                                        {t(`v2.defaulted.severity_${sv.key}`)}
                                    </p>
                                    <p className="text-[11px] text-gray-500">
                                        {sv.to ? t('v2.defaulted.range', { from: sv.from, to: sv.to }) : t('v2.defaulted.range_open', { from: sv.from })}
                                    </p>
                                    <p className="mt-2 text-xl font-black tabular-nums">{sv.count}</p>
                                    <p className="text-xs font-semibold tabular-nums text-gray-700 dark:text-zinc-300">{brl(sv.amount)}</p>
                                    <div className="mt-2 h-1.5 rounded-full bg-white/70 dark:bg-zinc-800">
                                        <div className={cn('h-1.5 rounded-full', SEVERITY_STYLE[sv.key].bar)} style={{ width: `${(sv.amount / maxSeverityAmount) * 100}%` }} />
                                    </div>
                                </button>
                            ))}
                        </div>
                    </section>

                    <SellerRanking rows={summary.top_sellers} onPick={(name) => setSearch(name)} />
                </>
            )}

            <section className="rounded-xl border border-gray-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 p-3 dark:border-zinc-800">
                    <label className="relative min-w-[220px] flex-1">
                        <PiMagnifyingGlassBold className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-gray-400" aria-hidden />
                        <input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t('v2.defaulted.search')}
                            className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm dark:border-zinc-700 dark:bg-zinc-900"
                        />
                    </label>
                    {(severity || status) && (
                        <Button type="button" size="sm" variant="outline" onClick={() => { setSeverity(''); setStatus(''); }}>
                            {t('v2.defaulted.clear_filters')}
                        </Button>
                    )}
                    <span className="text-xs text-gray-500">{t('v2.defaulted.rows', { count: rows.length })}</span>
                </div>

                {rows.length === 0 && !query.isLoading ? (
                    <div className="flex flex-col items-center gap-2 py-12 text-sm text-gray-500">
                        <PiWarningOctagonDuotone className="size-8 text-gray-300" aria-hidden />
                        {t('v2.defaulted.empty')}
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-xs">
                            <thead className="bg-gray-50 text-[10px] uppercase tracking-wide text-gray-500 dark:bg-zinc-800">
                                <tr>
                                    <SortTh label={t('v2.defaulted.col_sale')} sortKey="sale" sort={sort} onSort={toggle} />
                                    <SortTh label={t('v2.defaulted.col_client')} sortKey="client" sort={sort} onSort={toggle} />
                                    <SortTh label={t('v2.defaulted.col_seller')} sortKey="seller" sort={sort} onSort={toggle} />
                                    <SortTh label={t('v2.defaulted.col_installment')} sortKey="installment" sort={sort} onSort={toggle} />
                                    <SortTh label={t('v2.defaulted.col_due')} sortKey="due" sort={sort} onSort={toggle} />
                                    <SortTh label={t('v2.defaulted.col_amount')} sortKey="amount" sort={sort} onSort={toggle} align="right" />
                                    <SortTh label={t('v2.defaulted.col_days')} sortKey="days" sort={sort} onSort={toggle} align="right" />
                                    <SortTh label={t('v2.defaulted.col_status')} sortKey="status" sort={sort} onSort={toggle} />
                                    <SortTh label={t('v2.defaulted.col_forfeit')} sortKey="forfeit" sort={sort} onSort={toggle} />
                                    <SortTh label={t('v2.defaulted.col_commission')} sortKey="commission" sort={sort} onSort={toggle} align="right" />
                                    <th className="px-3 py-2.5" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100 dark:divide-zinc-800">
                                {sorted.map((r) => (
                                    <tr key={r.installment_id} className="hover:bg-gray-50/70 dark:hover:bg-zinc-800/40">
                                        <td className="px-3 py-2">
                                            {r.deal_url ? (
                                                <Link
                                                    to={`${r.deal_url}?tab=commission`}
                                                    title={t('v2.defaulted.open_deal')}
                                                    className="inline-flex items-center rounded-full bg-gray-900 px-2.5 py-0.5 font-bold tabular-nums text-white hover:bg-primary hover:text-gray-900 dark:bg-zinc-100 dark:text-zinc-900"
                                                >
                                                    {r.sale_number ?? '—'}
                                                </Link>
                                            ) : (
                                                <span className="inline-flex items-center rounded-full bg-gray-900 px-2.5 py-0.5 font-bold tabular-nums text-white dark:bg-zinc-100 dark:text-zinc-900">
                                                    {r.sale_number ?? '—'}
                                                </span>
                                            )}
                                            {r.sale_cancelled && <span className="ml-1 text-[10px] font-semibold text-rose-600">{t('v2.defaulted.sale_cancelled')}</span>}
                                        </td>
                                        <td className="max-w-[220px] truncate px-3 py-2" title={r.client ?? ''}>{r.client ?? '—'}</td>
                                        <td className="max-w-[160px] truncate px-3 py-2">{r.seller ?? '—'}</td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            {r.installment_label}
                                            {r.open_defaults_in_deal > 1 && (
                                                <span
                                                    className={cn('ml-1 rounded px-1 text-[10px] font-bold', r.open_defaults_in_deal >= 3 ? 'bg-violet-700 text-white' : 'bg-red-100 text-red-700')}
                                                    title={t('v2.defaulted.open_in_deal_hint')}
                                                >
                                                    {t('v2.defaulted.open_in_deal', { count: r.open_defaults_in_deal })}
                                                </span>
                                            )}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 tabular-nums">{fmtDate(r.due_date)}</td>
                                        <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums">{brl(r.amount)}</td>
                                        <td className="px-3 py-2 text-right">
                                            <span className={cn('inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 font-black tabular-nums', SEVERITY_STYLE[r.severity].badge)}>
                                                {t('v2.defaulted.days', { days: r.days_overdue })}
                                            </span>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2">
                                            <span className={cn(
                                                'rounded-full px-2 py-0.5 text-[10px] font-bold',
                                                r.status === 'forfeited' && 'bg-violet-100 text-violet-800',
                                                r.status === 'renegotiated' && 'bg-sky-100 text-sky-800',
                                                r.status === 'open' && 'bg-gray-100 text-gray-700 dark:bg-zinc-800 dark:text-zinc-200',
                                            )}
                                            >
                                                {t(`v2.defaulted.status_${r.status}`)}
                                            </span>
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 tabular-nums">
                                            {r.status === 'forfeited' ? '—' : fmtDate(r.forfeit_date)}
                                            {r.days_to_forfeit !== null && r.days_to_forfeit <= 15 && (
                                                <span className="ml-1 text-[10px] font-bold text-red-600">{t('v2.defaulted.soon', { days: r.days_to_forfeit })}</span>
                                            )}
                                        </td>
                                        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">{brl(r.commission_blocked)}</td>
                                        <td className="px-3 py-2 text-right">
                                            {r.deal_url && (
                                                <Link to={`${r.deal_url}?tab=commission`} className="inline-flex items-center gap-1 text-xs font-semibold text-gray-600 hover:text-gray-900" title={t('v2.defaulted.open_deal')}>
                                                    <PiArrowSquareOutBold className="size-4" aria-hidden />
                                                </Link>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>
        </div>
    );
}

/** Barra empilhada com a quantidade de parcelas em cada faixa de criticidade. */
function SeverityMix({ mix }: { mix: Record<Severity, number> }) {
    const total = Math.max(1, Object.values(mix).reduce((a, b) => a + b, 0));

    return (
        <div className="flex h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-zinc-800">
            {(['low', 'medium', 'high', 'critical'] as const).map((k) => (
                mix[k] > 0 ? <div key={k} className={SEVERITY_STYLE[k].bar} style={{ width: `${(mix[k] / total) * 100}%` }} title={`${mix[k]}`} /> : null
            ))}
        </div>
    );
}

function SellerRanking({ rows, onPick }: { rows: RankRow[]; onPick: (name: string) => void }) {
    const { t } = useI18n();
    const top = Math.max(1, rows[0]?.amount ?? 1);

    return (
        <SellerPodium
            title={t('v2.defaulted.ranking_title')}
            hint={t('v2.defaulted.ranking_hint')}
            onPick={(row) => onPick(row.name)}
            rows={rows.map((row) => ({
                key: `${row.position}-${row.seller}`,
                position: row.position,
                name: row.seller,
                avatarUrl: row.avatar_url,
                value: brl(row.amount),
                meta: t('v2.defaulted.rank_meta', { count: row.count, deals: row.deals }),
                detail: t('v2.defaulted.rank_line', { count: row.count, deals: row.deals, days: row.max_days, share: row.share_pct }),
                ratio: row.amount / top,
                trailing: <SeverityMix mix={row.severity_mix} />,
            }))}
        />
    );
}
